import React, { useRef, useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import { ChevronLeft, LogIn, Plus, SearchIcon, Send, X } from "lucide-react";
import { Autocomplete, Backdrop, InputAdornment } from "@mui/material";
import { styled } from "@mui/material/styles";
import Checkbox from "@mui/material/Checkbox";
import Radio from "@mui/material/Radio";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import { Link } from "react-router-dom";
import { FaPlus } from "react-icons/fa";
import Select from "react-select";
import { motion, AnimatePresence } from "framer-motion";

const ReminderModal = ({ isOpen, onClose, onSubmit, defaultCensusYear }) => {
  const [message, setMessage] = useState("");
  const [censusYear, setCensusYear] = useState("");

  useEffect(() => {
    if (isOpen) {
      setMessage("");
      setCensusYear(defaultCensusYear || "");
    }
  }, [isOpen, defaultCensusYear]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white p-6 rounded shadow-lg w-96"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold mb-4">Add Reminder</h2>

        <label className="block mb-2 font-semibold">Message</label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="border rounded w-full px-3 py-2 mb-4 resize-none"
          rows={4}
          placeholder="Enter your message here"
        />

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 bg-gray-300 rounded">
            Cancel
          </button>
          <button
            onClick={() => {
              onSubmit({ censusYear, message });
              onClose();
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded"
            disabled={!message.trim()}
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReminderModal;
// export default ReminderModal;

// RejectModal
export const RejectModal = ({
  isOpen,
  reason,
  rejection_remarks,
  onClose,
  onChangeReason,
  onSubmit,
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black z-40"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          />

          {/* Modal */}
          <motion.div
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0, scale: 0.9, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 30 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <motion.div
              className="bg-white rounded-lg w-full max-w-md shadow-lg"
              layout
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Reject Stage</h3>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-[Regular] text-[#565656] mb-1">
                    Reason for Rejection <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    disabled={!!rejection_remarks}
                    value={rejection_remarks ?? reason}
                    onChange={(e) => {
                      if (!rejection_remarks) onChangeReason(e.target.value);
                    }}
                    placeholder="Please provide a detailed reason for rejection..."
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md 
                  focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    rejection_remarks ? "text-red-600 italic" : "text-[#565656]"
                  } font-[Regular] outline-none text-sm`}
                    rows={4}
                  />
                </div>

                {!rejection_remarks && (
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={onClose}
                      className="bg-gray-300 text-gray-800 rounded-lg px-4 py-2 
                                hover:bg-gray-400 transition-colors text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={onSubmit}
                      className="bg-teal-600 text-white rounded-lg px-4 py-2 
                                hover:bg-teal-700 transition-colors text-sm"
                    >
                      Submit
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};


export const CustomTextField = ({
  register,
  errors,
  label,
  helperText,
  id,
  ...props
}) => {

  return (
    <div className="mb-[1.3rem] px-2 w-full">
      <TextField
        {...register}
        id={id}
        label={label}
        variant="outlined"
        size="small"
        helperText={errors ? errors.message : helperText}
        error={!!errors}
        {...props}
      />
    </div>
  );
};

export const MultiSelectDropdown = ({
  label,
  options,
  selectedValues,
  onChange,
  placeholder = "Select...",
  isDisabled = false,
}) => {
  const handleChange = (selectedOptions) => {
    onChange(
      selectedOptions ? selectedOptions.map((option) => option.value) : []
    );
  };

  const selectedOptions = options.filter((option) =>
    selectedValues.includes(option.value)
  );

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <Select
        isMulti
        options={options}
        value={selectedOptions}
        onChange={handleChange}
        placeholder={placeholder}
        isDisabled={isDisabled}
        className="basic-multi-select"
        classNamePrefix="select"
      />
    </div>
  );
};
export const ThinTextAreaField = ({
  placeHolder,
  label,
  id,
  name,
  error,
  helperText,
  isRequired = true,
  value,
  readOnly,
  onChange,
  rows = 4, // ✅ default rows
  className = "",
  ...props
}) => {
  return (
    <div className={`flex flex-col ${className || "w-full"}`}>
      <label
        htmlFor={id}
        className="text-sm font-[Regular] text-[#565656] mb-1"
      >
        {label}
        {isRequired && !readOnly && !props.disabled && <span className="text-red-500"> *</span>}
      </label>

      <textarea
        id={id}
        name={name}
        rows={rows}
        className={`bg-[#ebeef2] focus:bg-[#dde2e9] border ${
          error ? "border-red-500" : "border-[#cbd5e1]"
        } text-[#565656] font-[Regular] outline-none text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full px-3 py-2 resize-none`}
        placeholder={placeHolder}
        value={value}
        readOnly={readOnly}
        onChange={onChange}
        {...props}
      />
      {error && (
        <span className="text-red-500 text-[0.75rem] mt-1">{helperText}</span>
      )}
    </div>
  );
};

export const ThinTextField = ({
  placeHolder,
  type = "text",
  label,
  id,
  name,
  error,
  helperText,
  isRequired = true,
  value,
  readOnly,
  onChange,
  className = "",
  ...props
}) => {
  return (
    <div className={`flex flex-col ${className || "w-full"}`}>
      <label
        htmlFor={id}
        className="text-sm font-[Regular] text-[#565656] mb-1"
      >
        {label}
        {isRequired && !readOnly && !props.disabled && <span className="text-red-500"> *</span>}
      </label>

      <input
        id={id}
        name={name}
        className={`bg-[#ebeef2] focus:bg-[#dde2e9] border ${
          error ? "border-red-500" : "border-[#cbd5e1]"
        } text-[#565656] font-[Regular] outline-none text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full px-3 py-2 ${
          type === "number" ? "hide-number-arrows" : ""
        }`}
        type={type}
        placeholder={placeHolder}
        value={value}
        readOnly={readOnly}
        onChange={(e) => {
          // prohibit invalid number
          if (type === "number") {
            if (e.target.value.startsWith("-")) return;
          }
          if (onChange) {
            onChange(e);
          }
        }}
        {...props}
      />
      {error && (
        <span className="text-red-500 text-[0.75rem] mt-1">{helperText}</span>
      )}
    </div>
  );
};

export const ThinDropdown = ({
  color = "#565656",
  label,
  id,
  options,
  error,
  helperText,
  required = true,
  className = "",
  value,
  onChange,
  placeholder,
  ...props
}) => {
  const hasEmptyOption = options?.some((option) => option.value === "" || option.value == null);
  const placeholderText =
    placeholder ??
    (typeof label === "string" && label.trim()
      ? `Select ${label.trim().replace(/^select\s+/i, "")}`
      : "Select");
  return (
    <div className={`flex flex-col py-[0.25rem] ${className || "w-full"}`}>
      <label
        htmlFor={id}
        className="text-sm font-[Regular] text-[#565656] mb-1"
      >
        {label}
        {required && !props.disabled && !props.readOnly && <span className="text-red-500"> *</span>}
      </label>
      <select
        id={id}
        value={value ?? ""}
        onChange={(e) => onChange && onChange(e)}
        className={`bg-[#ebeef2] focus:bg-[#dde2e9] border ${
          error ? "border-red-500" : "border-[#cbd5e1]"
        } text-[#565656] font-[Regular] outline-none text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full px-3 py-2`}
        {...props}
      >
        {!hasEmptyOption && <option value="">{placeholderText}</option>}
        {options?.map((option, index) => (
          <option key={`${option.value}-${index}`} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {error && (
        <span className="text-red-500 text-[0.75rem] mt-1">{helperText}</span>
      )}
    </div>
  );
};

export const PaginationDropdown = ({ value, onChange, options }) => {
  return (
    <div className="flex items-center gap-2 bg-[#f1f4f8] border-[#ebeef2] border-[1px] rounded-md ">
      {/* <label className="text-gray-700 text-[0.8rem]">Page Size:</label> */}
      <select
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="border custom-select  bg-[#f1f4f8] text-[#576069] border-none outline-none rounded-md px-2 py-1 text-[14px]"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

// export const ThinDropdownWithSearch = ({
//   label,
//   id,
//   options,
//   value,
//   onChange,
//   error,
//   helperText,
//   required = true, // Add a required prop for conditionally showing the asterisk
//   placeholder = "Select an option", // Add a placeholder prop
//   ...props
// }) => {
//   // Include a placeholder option
//   const enhancedOptions = [{ value: "", label: placeholder }, ...options];

//   // Find the selected option based on the value
//   const selectedOption =
//     enhancedOptions.find((option) => option.value === value) || null;

//   return (
//     <div className="flex flex-col w-full mt-1">
//       <label className="text-[0.8rem] mt-2 mb-1 font-[InterLight]">
//         {label}
//         {required && <span className="text-red-500"> *</span>}
//       </label>

//       <Autocomplete
//         id={id}
//         disablePortal
//         options={enhancedOptions}
//         value={selectedOption} // Set the whole object as value
//         onChange={(event, newValue) => {
//           // Pass only the value of the selected option to the onChange
//           onChange(newValue ? newValue.value : "");
//         }}
//         getOptionLabel={(option) => option.label || ""}
//         isOptionEqualToValue={(option, value) => option.value === value.value}
//         renderInput={(params) => (
//           <TextField
//             {...params}
//             error={!!error} // Display error styling if there is an error
//             placeholder={placeholder} // Add placeholder to the input field
//           />
//         )}
//         {...props}
//       />

//       {error && (
//         <span className="text-red-500 text-[0.75rem] mt-1">{helperText}</span>
//       )}
//     </div>
//   );
// };

export const ThinDropdownWithSearch = ({
  label,
  id,
  options,
  value,
  onChange,
  error,
  helperText,
  required = true,
  placeholder = "Select an option",
  disabled = false,
  ...props
}) => {
  const placeholderOption = { value: "", label: placeholder, disabled: true };
  const enhancedOptions = [placeholderOption, ...options];

  const selectedOption =
    enhancedOptions.find((option) => option.value === value) ||
    placeholderOption;

  const handleChange = (e) => {
    if (!disabled && onChange) {
      onChange(e.target.value);
    }
  };

  return (
    <div className="flex flex-col w-full">
      <label className="text-sm font-[Regular] text-[#565656] mb-1">
        {label}
        {required && !disabled && <span className="text-red-500"> *</span>}
      </label>

      <select
        value={value}
        onChange={handleChange}
        disabled={disabled}
        className={`bg-[#ebeef2] focus:bg-[#dde2e9] border ${
          error ? "border-red-500" : "border-[#cbd5e1]"
        } text-[#565656] font-[Regular] outline-none text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full px-3 py-2 ${
          disabled
            ? "cursor-not-allowed opacity-60 bg-gray-200"
            : "cursor-pointer"
        }`}
        {...props}
      >
        <option value="" className="font-[Regular] text-[#565656]">
          {placeholder}
        </option>
        {options.map((opt) => (
          <option
            className="font-[Regular] text-[#565656]"
            key={opt.value}
            value={opt.value}
          >
            {opt.label}
          </option>
        ))}
      </select>

      {error && (
        <span className="text-red-500 text-[0.75rem] mt-1">{helperText}</span>
      )}
    </div>
  );
};

export const ThinDropdownWithSearchYear = ({
  label,
  id,
  options,
  value, // Should be an object { value: "2028", label: "2028" }
  onChange,
  error,
  helperText,
  required = true,
  ...props
}) => {
  // Include a placeholder option
  const enhancedOptions = [{ value: "", label: "" }, ...options];

  // Find the selected option based on the value
  const selectedOption =
    enhancedOptions.find((option) => option.value === value?.value) || null;

  return (
    <div className="flex flex-col w-full mt-1">
      <label className="text-[0.8rem] mt-2 mb-1 font-[InterLight]">
        {label}
        {required && !props.disabled && !props.readOnly && <span className="text-red-500"> *</span>}
      </label>

      <Autocomplete
        id={id}
        disablePortal
        options={enhancedOptions}
        value={selectedOption} // Set the whole object as value
        onChange={(event, newValue) => {
          onChange(newValue ? newValue.value : ""); // Ensure we set only the `value`
        }}
        getOptionLabel={(option) => option.label || ""}
        isOptionEqualToValue={(option, value) => option?.value === value?.value}
        renderInput={(params) => <TextField {...params} error={!!error} />}
        {...props}
      />

      {error && (
        <span className="text-red-500 text-[0.75rem] mt-1">{helperText}</span>
      )}
    </div>
  );
};

export const SubmitButton = ({ type = "button", text, icon, ...props }) => {
  return (
    <div className="mt-4 flex justify-end items-end w-full">
      <button
        type={type}
        className="py-[8px] flex gap-2 items-center px-4 rounded border-[#137E81] text-[14px] bg-[#137E81] hover:bg-[#135b5d] hover:border-[#0e743e] text-white font-[InterLight]"
        {...props}
      >
        {icon && <span className="w-4">{icon}</span>}
        {text}
      </button>
    </div>
  );
};

export const AddButton = ({
  type,
  text,
  isLoading,
  icon: Icon = Send,
  ...props
}) => {
  const Loader = () => (
    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
  );

  return (
    <div className="flex justify-end">
      <button
        type={type}
        disabled={isLoading || props.disabled}
        className={`
          py-[0.375rem] px-[0.75rem] rounded font-[InterLight] lg:text-[15px] text-[12px] 
          ${type === "Submit" ? "bg-primaryDark" : "bg-[#be9609]"}
          text-white w-auto opacity-95 hover:opacity-85 
          border-green flex items-center gap-2 
          ${isLoading ? "cursor-not-allowed opacity-80" : ""}
        `}
        {...props}
      >
        {isLoading ? (
          <>
            <Loader />
            Submitting..
          </>
        ) : (
          <>
            {text === "Add" && <FaPlus className="w-3 h-[13px]" />}
            {text}
          </>
        )}
      </button>
    </div>
  );
};

export const ViewField = ({ label, value }) => (
  <div className="flex flex-col gap-1 w-full">
    <label className="text-sm text-gray-600">{label}</label>
    <div className="p-2 border border-gray-300 rounded-md bg-gray-50">
      {value || "-"}
    </div>
  </div>
);

export const BackButton = ({ to, text, ...props }) => {
  return (
    <div className="flex justify-end">
      <Link to={to} className="w-auto">
        <button
          className="py-[0.375rem] px-[0.75rem] rounded-[0.375rem] font-[InterLight] text-[14px] bg-[#4B5563] text-white opacity-95 hover:opacity-85 border-green"
          {...props}
        >
          <span className="flex items-center gap-2">
            <ChevronLeft size={13} /> {text}
          </span>
        </button>
      </Link>
    </div>
  );
};

export const CustomConfirmModal = ({
  isVisible,
  onConfirm,
  onCancel,
  message,
}) => {
  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-md w-auto">
        <h2 className="text-md font-[Inter] font-bold text-center">
          {message}
        </h2>
        <div className="mt-4 flex justify-center gap-4">
          {/* YES button -> RED */}
          <button
            onClick={onConfirm}
            className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
          >
            Yes
          </button>

          {/* NO button -> PrimaryDark */}
          <button
            onClick={onCancel}
            className="bg-primaryDark text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            No
          </button>
        </div>
      </div>
    </div>
  );
};


export const ToggleButton = ({ isChecked, onToggle, disabled = false }) => {
  const [isOn, setIsOn] = useState(isChecked || false);

  const handleToggle = () => {
    if (!disabled) {
      setIsOn((prev) => !prev);
      // Call `onToggle` only to update parent state, not trigger the API.
      onToggle?.(!isOn);
    }
  };

  return (
    <div
      onClick={handleToggle}
      role="switch"
      aria-pressed={isOn}
      className={`w-9 h-4 flex items-center rounded-full cursor-pointer p-[2px] ${
        isOn
          ? "bg-lightGray border border-muted"
          : "bg-gray-300 border border-muted"
      } ${
        disabled ? "cursor-not-allowed opacity-50" : ""
      } transition-colors duration-300`}
    >
      <div
        className={`h-3 w-3 rounded-full bg-primaryDark shadow-md transform transition-transform duration-300 ${
          isOn ? "translate-x-[1.1rem]" : "translate-x-0"
        }`}
      ></div>
    </div>
  );
};

export const NICInputField = ({
  value = "",
  onChange,
  label,
  error,
  helperText,

  ...props
}) => {
  const handleInputChange = (e) => {
    let input = e.target.value.replace(/\D/g, ""); // Remove non-digit characters
    if (input.length > 5) input = `${input.slice(0, 5)}-${input.slice(5)}`;
    if (input.length > 13) input = `${input.slice(0, 13)}-${input.slice(13)}`;
    onChange(input); // Update the masked value to the parent
  };

  return (
    <div className="flex flex-col w-full py-[0.25rem]">
      {label && (
        <label className="text-[0.8rem] text-[#565656] mt-2 mb-1 font-[InterLight]">
          {label}{" "}
        </label>
      )}
      <input
        type="text"
        value={value}
        onChange={handleInputChange}
        className={`text-[15px] py-2 px-4 font-[InterLight] text-[#565656] bg-[#ebeef2] focus:bg-[#dde2e9] outline-none border-[#dde2e9] border rounded-[0.35rem] ${
          error ? "border-red-500" : ""
        }`}
        placeholder="12345-1234567-1"
        maxLength={15} // Limit to NIC format
        {...props} // Pass down additional props like id, name, etc.
      />
      {error && (
        <span className="text-red-500 text-[0.75rem] mt-1">{helperText}</span>
      )}
    </div>
  );
};

export const PhoneInputField = ({
  value,
  onChange,
  label,
  error,
  isRequired = true,
  helperText,
  ...props
}) => {
  const inputRef = useRef(null);

  const handleInputChange = (e) => {
    const rawValue = e.target.value;
    const digitsOnly = rawValue.replace(/\D/g, "").slice(0, 11);

    onChange(digitsOnly); // Pass only digits to Formik
  };

  // Format only for display — no side effects here
  const getFormattedValue = (val) => {
    const digits = val.replace(/\D/g, "").slice(0, 11);
    if (digits.length > 4) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
    return digits;
  };

  return (
    <div className=" flex flex-col w-full py-[0.25rem]">
      <label className="text-[0.8rem] text-[#565656] mt-2 mb-1 font-[InterLight]">
        {label}
        {isRequired && !props.disabled && !props.readOnly && <span className="text-red-500"> *</span>}
      </label>
      <input
        ref={inputRef}
        type="text"
        value={getFormattedValue(value)}
        onChange={handleInputChange}
        className="text-[15px]   font-[InterLight] text-[#565656] bg-[#ebeef2] focus:bg-[#dde2e9] py-2 px-4 outline-none border-[#dde2e9] border rounded-[0.35rem]"
        placeholder="0300-0000000"
        maxLength={12} // 11 digits + 1 hyphen
        {...props}
      />
      {error && (
        <span className="text-red-500 text-[0.75rem] mt-1">{helperText}</span>
      )}
    </div>
  );
};

export const CustomCheckbox = ({
  checked,
  onChange,
  label,
  error,
  helperText,
  ...props
}) => {
  // Custom unchecked icon styles
  const CustomIcon = styled("span")(({ theme }) => ({
    borderRadius: "4px",
    width: 17,
    height: 17,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f0f0f0", // Light gray background
    border: "1px solid #d0d0d0", // Gray border
    transition: "background-color 0.3s",
    "input:hover ~ &": {
      backgroundColor: "#e0e0e0", // Slightly darker on hover
    },
  }));

  // Custom checked icon styles
  const CustomCheckedIcon = styled(CustomIcon)({
    backgroundColor: "#0d47a1", // Dark blue background
    borderColor: "#0d47a1", // Match border color with background
    "&::before": {
      content: '""',
      position: "relative",

      width: 13,
      height: 13,
      backgroundImage:
        "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='m6 10 3 3 6-6'/%3e%3c/svg%3e\")",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center center",
      display: "block",
    },
    "input:hover ~ &": {
      backgroundColor: "rgb(15, 55, 113)", // Darker blue on hover
    },
  });

  return (
    <div className="flex  w-full mb-4">
      <Checkbox
        checked={checked}
        onChange={onChange}
        disableRipple
        color="default"
        checkedIcon={<CustomCheckedIcon />}
        icon={<CustomIcon />}
        inputProps={{ "aria-label": "Custom checkbox" }}
        {...props}
      />
      {label && (
        <label className="text-[0.8rem] mt-2 mb-1 font-[InterLight]">
          {label}
        </label>
      )}
      {error && (
        <span className="text-red-500 text-[0.75rem] mt-1">{helperText}</span>
      )}
    </div>
  );
};

export const CustomRadioButton = ({
  label,
  options = [],
  selectedValue, // This should hold the selected value
  onChange,
  error,
  helperText,
  ...props
}) => {
  // Custom radio styles
  const CustomIcon = styled("span")`
    width: 17px;
    height: 17px;
    border-radius: 50%;
    border: 1.5px solid #d0d0d0;
    display: flex;
    align-items: center;
    justify-content: center;
  `;

  const CustomCheckedIcon = styled(CustomIcon)`
    background-color: #0d47a1;
    border-color: #0d47a1;
    &::before {
      content: "";
      display: block;
      width: 9px;
      height: 9px;
      border-radius: 50%;
      background-color: #fff;
    }
  `;

  return (
    <div className="mb-4 w-full">
      {label && (
        <label className="text-[0.8rem] font-[InterLight] block mb-1">
          {label} {!props.disabled && <span className="text-red-500">*</span>}
        </label>
      )}

      <div className="flex gap-4">
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={
              <Radio
                checked={selectedValue === option.value} // Correct checked condition
                onChange={(e) => onChange(option.value)} // Correct value update
                disableRipple
                color="default"
                icon={<CustomIcon />}
                checkedIcon={<CustomCheckedIcon />}
                {...props}
              />
            }
            label={option.label}
          />
        ))}
      </div>

      {error && (
        <FormHelperText className="text-red-500">{helperText}</FormHelperText>
      )}
    </div>
  );
};

export const ThinTextarea = ({
  placeHolder,
  label,
  id,
  name,
  error,
  helperText,
  isRequired = true,
  value,
  onChange,
  rows = 4,
  className = "",
  ...props
}) => {
  return (
    <div className={`flex flex-col ${className || "w-full"}`}>
      {label && (
        <label
          htmlFor={id}
          className="text-sm font-[Regular] text-[#565656] mb-1"
        >
          {label}
          {isRequired && !props.disabled && !props.readOnly && <span className="text-red-500"> *</span>}
        </label>
      )}

      <textarea
        id={id}
        name={name}
        rows={rows}
        className={`bg-[#ebeef2] focus:bg-[#dde2e9] border ${
          error ? "border-red-500" : "border-[#cbd5e1]"
        } text-[#565656] font-[Regular] outline-none text-sm rounded-md 
        focus:ring-blue-500 focus:border-blue-500 block w-full px-3 py-2 resize-none`}
        placeholder={placeHolder}
        value={value}
        onChange={onChange}
        {...props}
      />

      {error && (
        <span className="text-red-500 text-[0.75rem] mt-1">{helperText}</span>
      )}
    </div>
  );
};